/**
 * @license
 * Copyright (c) 2008 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MarkerShapes } from '../UNSAFE_visTypes/common';
import { Point } from '../PRIVATE_visSVGUtils';
import { curveThroughPoints } from '../PRIVATE_visSVGUtils/pathUtils';
import { Scale, ChartItemData, Group } from '../UNSAFE_visTypes/chart';
import { ColorProps } from '../UNSAFE_interpolations/colors';
import { colorSchemeVars } from '../../Common/themes/themeContract.css';
import { rgb, rgba } from '../../UNSAFE_Theme';
import { getStraightAreaPath, getCurveAreaPath } from '../PRIVATE_visSVGUtils/areaUtils';
import { getGroupCenterCoord } from '../../utils/PRIVATE_chartUtils/layoutUtils';
import { LineAreaItem } from '../../UNSAFE_LineAreaChart/lineAreaChart.types';

export function getCmdsForArea(
  lineSegmentCoords: Point[],
  bottomCoords: Point[],
  areaType: 'straight' | 'curved',
  isHoriz: boolean,
  isBottomSegmentCurved: boolean
) {
  if (areaType === 'straight') {
    return getStraightAreaPath(lineSegmentCoords, bottomCoords);
  }
  return getCurveAreaPath(
    lineSegmentCoords,
    bottomCoords,
    false,
    isHoriz ? 'mh' : 'mv',
    isBottomSegmentCurved
  );
}

export function getCmdsForLine(
  lineSegmentCoords: Point[],
  lineType: 'straight' | 'curved',
  isHoriz: boolean
) {
  if (lineType === 'straight') {
    return lineSegmentCoords.join(' ');
  }
  return curveThroughPoints(lineSegmentCoords, false, isHoriz ? 'mh' : 'mv');
}
/**
 * Returns the cumulative series for stacked chart.
 */
export function getCumulativeSeries<K extends string | number, D extends ChartItemData<K>>(
  numSeries: number,
  numGroups: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  hiddenIds: Set<number | string>,
  isLog: boolean
) {
  const series: number[][] = [];
  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
    const currentSeries: number[] = [];
    const prevSeries = series[seriesIndex - 1];
    for (let groupIndex = 0; groupIndex < numGroups; groupIndex += 1) {
      const item = getDataItem(seriesIndex, groupIndex);
      if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
        continue;
      }
      if (seriesIndex) {
        currentSeries[groupIndex] = item.value + prevSeries[groupIndex];
      } else {
        currentSeries[groupIndex] = item.value;
      }
    }
    series.push(currentSeries);
  }
  return series;
}

/**
 * Returns the series data.
 */
export function getSeriesData<K extends string | number, D extends ChartItemData<K>>(
  numSeries: number,
  numGroups: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  hiddenIds: Set<number | string>,
  isLog: boolean
) {
  const series: number[][] = [];
  for (let seriesIndex = 0; seriesIndex < numSeries; seriesIndex += 1) {
    const currentSeries: number[] = [];
    for (let groupIndex = 0; groupIndex < numGroups; groupIndex += 1) {
      const item = getDataItem(seriesIndex, groupIndex);
      if (!item || hiddenIds?.has(item.id) || (isLog && item.value <= 0)) {
        continue;
      }
      currentSeries[groupIndex] = item.value;
    }
    series.push(currentSeries);
  }
  return series;
}

/**
 * Returns the markertype order.
 */
export function getMarkers(): MarkerShapes[] {
  return ['square', 'circle', 'diamond', 'plus', 'triangleDown', 'triangleUp'];
}

/**
 * Returns the marker fill and stroke depending on the state.
 */
export function getMarkerFillAndStroke( // TODO : JET-62244 - Replace getMarkerFillAndStroke function with classes.
  isMarkerDisplayed: boolean,
  isSelectionEnabled: boolean,
  isHovered: boolean,
  isFocused: boolean,
  isSelected: boolean,
  seriesColor: ColorProps['color'],
  itemColor?: ColorProps['color']
) {
  let fill;
  let stroke;
  let outerStroke;

  if (isSelectionEnabled) {
    if (isSelected) {
      if (isMarkerDisplayed) {
        // TODO: settle on one visual for selected markers: 
        // Selected effect when markers are turned on
        fill = itemColor || seriesColor;
        outerStroke = rgb(colorSchemeVars.palette.neutral[130]);
        stroke = rgb(colorSchemeVars.palette.neutral[0]);
      } else {
        // Selected effect when markers are not turned on
        fill = rgb(colorSchemeVars.palette.neutral[0]);
        if (isHovered || isFocused) {
          stroke = itemColor || seriesColor;
        } else {
          stroke = rgb(colorSchemeVars.palette.neutral[130]);
        }
      }
    } else if (isHovered || isFocused) {
      stroke = itemColor || seriesColor;
      fill = rgb(colorSchemeVars.palette.neutral[0]);
    } else {
      if (isMarkerDisplayed) {
        fill = itemColor || seriesColor;
        stroke = rgb(colorSchemeVars.palette.neutral[0]);
      } else {
        fill = rgba(colorSchemeVars.palette.neutral[0], 0);
        stroke = rgba(colorSchemeVars.palette.neutral[0], 0);
      }
    }
  } else if (isMarkerDisplayed) {
    fill = itemColor || seriesColor;
    stroke = rgb(colorSchemeVars.palette.neutral[0]);
  } else {
    stroke = isFocused ? itemColor || seriesColor : rgba(colorSchemeVars.palette.neutral[0], 0);
    fill = rgba(colorSchemeVars.palette.neutral[0], 0);
  }
  return { fill, stroke, outerStroke };
}

/**
 * Returns the coordinates of line or area based on orientation and size of the chart.
 */
export function getLineAreaSegments<K extends string | number, D extends LineAreaItem<K>>(
  yScale: Scale,
  xScale: Scale,
  data: number[],
  startIndex: number,
  endIndex: number,
  isHorizontal: boolean,
  isLog: boolean,
  groups: Group[],
  seriesIndex: number,
  getDataItem: (seriesIndex: number, groupIndex: number) => D | undefined,
  timeAxisType?: 'enabled' | 'skipGaps' | 'mixedFrequency'
): Point[][] {
  const segments: Point[][] = [];
  let currentPath: Point[] = [];

  for (let groupIndex = startIndex; groupIndex < endIndex + 1; groupIndex += 1) {
    const item = data[groupIndex];

    if (item == null || (isLog && item <= 0)) {
      segments.push(currentPath);
      currentPath = [];
      continue;
    }
    const x = isHorizontal
      ? yScale.transform(item)
      : getGroupCenterCoord<K, D>(
          groups[groupIndex],
          groupIndex,
          xScale,
          getDataItem(seriesIndex, groupIndex)!,
          timeAxisType
        );
    const y = isHorizontal
      ? getGroupCenterCoord<K, D>(
          groups[groupIndex],
          groupIndex,
          xScale,
          getDataItem(seriesIndex, groupIndex)!,
          timeAxisType
        )
      : yScale.transform(item);
    currentPath.push([x, y]);
    if (groupIndex === endIndex) {
      segments.push(currentPath);
    }
  }
  return segments;
}
